import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { ChevronLeft, Loader2 } from "lucide-react";
import { apiFetch } from "@/utils";
import BuyTokensForm from "@/components/BuyTokensForm";

declare global {
  interface Window {
    Android?: {
      onRedirectDetected: (url: string) => void;
    };
  }
}

export default function BuyTokensPage() {
  const [, navigate] = useLocation();
  const [checkoutUrl, setCheckoutUrl] = useState<string | undefined>(undefined);
  const [isRedirectPayment, setIsRedirectPayment] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>("");

  const handleBuyTokens = async (
    fiatAmount: string,
    cryptoAmount: string,
    paymentMethod: "debit-credit-card" | "apple-pay" | "google-pay"
  ) => {
    try {
      setPaymentMethod(paymentMethod);

      const createOrderResponse = await apiFetch("/v1/tokenPurchase/buy", {
        method: "POST",
        withCredentials: true,
        body: JSON.stringify({
          fiatAmount,
          cryptoAmount,
          paymentMethod,
        }),
      });

      const createOrderResult = await createOrderResponse.json();

      const checkoutUrl = createOrderResult.data.checkoutUrl;

      // Check if payment method requires redirect
      const requiresRedirect =
        paymentMethod === "apple-pay" ||
        paymentMethod === "google-pay" ||
        paymentMethod === "debit-credit-card";

      // Debugging
      console.log("checkoutUrl", checkoutUrl, "paymentMethod", paymentMethod);

      if (requiresRedirect) {
        setIsRedirectPayment(true);
        setIsRedirecting(true);

        // Small delay to show the redirect message
        setTimeout(() => {
          // Tell Android we're redirecting to payment provider
          if (window.Android?.onRedirectDetected) {
            window.Android.onRedirectDetected(checkoutUrl);
          }

          window.location.href = checkoutUrl;

          // After redirect happens, switch to "Payment in Progress" state
          // so users don't see misleading "Redirecting..." when they return
          setTimeout(() => {
            setIsRedirecting(false);
          }, 3000);
        }, 1500);
      } else {
        // Use iframe for card payments
        setCheckoutUrl(checkoutUrl);
        setIsRedirectPayment(false);
      }
    } catch (error: any) {
      console.error(`Can not process the order. Reason: ${error?.message}`);
      setIsRedirecting(false);
      setIsRedirectPayment(false);
    }
  };

  const handleNewPayment = () => {
    setCheckoutUrl(undefined);
    setIsRedirectPayment(false);
    setIsRedirecting(false);
    setPaymentMethod("");
  };

  // Handle return from external payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get("payment_status");
    const paymentResult = urlParams.get("payment_result");

    if (paymentStatus || paymentResult) {
      // Handle payment return - you might want to show success/failure state
      console.log("Payment returned:", { paymentStatus, paymentResult });
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const getPaymentMethodDisplayName = (method: string) => {
    switch (method) {
      case "apple-pay":
        return "Apple Pay";
      case "google-pay":
        return "Google Pay";
      default:
        return "your selected payment method";
    }
  };

  return (
    <div className="min-h-screen bg-[#18181B] flex flex-col">
      <div className="max-w-5xl mx-auto px-5 pb-8 h-[100%] flex flex-col">
        {/* Header with back button */}
        <div
          role="banner"
          className="h-12 flex items-center justify-center text-white mb-4 pt-3 relative"
        >
          <button
            type="button"
            aria-label="Go back"
            onClick={() => navigate("/")}
            className="absolute left-0 h-8 w-8 min-h-0 min-w-0 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
          >
            <ChevronLeft className="text-white h-[22px] w-[22px]" />
          </button>

          <h1 className="text-base leading-[18px] font-semibold tracking-wide">
            Buy Tokens
          </h1>
        </div>

        {/* Page Header */}
        <div className="flex flex-col gap-2 text-start mb-5">
          <h1 className="text-base leading-[18px] font-semibold font-figtree text-[#ffffff]">
            Buy SECH Tokens
          </h1>
          <p className="text-[13px] leading-[15px] font-regular text-[#BABABB]">
            Easily convert USD into SECH using your preferred payment method
          </p>
        </div>

        {!checkoutUrl && !isRedirectPayment ? (
          <div className="h-full">
            <BuyTokensForm onBuyTokens={handleBuyTokens} />
          </div>
        ) : isRedirectPayment ? (
          <Card className="py-4 px-3 rounded-[12px] text-center flex flex-col gap-4 bg-[#2F2F32] border-0">
            {isRedirecting ? (
              <>
                <div className="flex justify-center">
                  <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
                <h2 className="text-base leading-[18px] font-semibold text-[#ffffff]">
                  Redirecting to {getPaymentMethodDisplayName(paymentMethod)}
                </h2>
                <p className="text-[13px] leading-[15px] text-[#BABABB]">
                  Please wait while we redirect you to complete your payment
                  securely.
                </p>
                <div className="bg-black/20 p-4 rounded-lg">
                  <p className="text-[13px] leading-[15px] text-[#BABABB]">
                    You'll be taken to the payment provider to complete your
                    transaction. After payment, you'll be returned to this app.
                  </p>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-base leading-[18px] font-semibold text-[#ffffff]">
                  Payment in Progress
                </h2>
                <p className="text-[13px] leading-[15px] text-[#BABABB]">
                  Complete your {getPaymentMethodDisplayName(paymentMethod)}{" "}
                  payment and return to the app.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={handleNewPayment}
                    className="w-full bg-black/20 text-sm text-[#ffffff] px-4 py-2 rounded-md hover:bg-black/30"
                  >
                    Start New Payment
                  </button>
                  <p className="text-xs text-[#BABABB]">
                    Only use "Start New Payment" if your payment was cancelled
                    or failed
                  </p>
                </div>
              </>
            )}
          </Card>
        ) : (
          <div className="space-y-4">
            <iframe
              src={checkoutUrl}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-presentation"
              allow="camera; microphone; payment; encrypted-media; midi"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ borderRadius: "16px", width: "100%", minHeight: "80vh" }}
            />
            <div className="text-center">
              <button
                onClick={handleNewPayment}
                className="text-sm text-[#BABABB] hover:text-[#ffffff] underline"
              >
                Start Over
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
